/**
 * Data layer hook pro Graph2 — orchestruje:
 *   1. `useChartData` (tile cache + fetch)
 *   2. `useTopics` jen pro topic metadata (decimals, valueType, unit, nickname, suspicious)
 *   3. pre-built indexed `Map<timestamp, row>` pro O(1) tooltip lookup
 *   4. pre-built `chartData` array pro Recharts (žádný includes/find O(N²))
 *   5. min/max přes `reduce` (žádný `Math.min(...spread)` stack overflow)
 *
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 3).
 */

import { useMemo } from "react";
import { useChartData } from "@client/hooks/useChartData";
import { useTopics } from "@client/hooks/useTopics";
import type { ChartResolutionKey } from "@client/lib/chartBucketing";
import type { ChartPoint } from "@client/store/chart";
import type { Bounds } from "./Graph2Interaction";

export interface ChartRow {
  timestamp: number;
  /** Per topicId: hodnota (avg pro bucketed). */
  [topicId: string]: number | undefined;
}

export interface Graph2DataResult {
  /** Per-topic body sloučené z tiles + live tail. */
  series: Map<string, ChartPoint[]>;
  resolution: Map<string, ChartResolutionKey>;
  /** Recharts `data` prop — pole rows sorted by timestamp, jeden key per topic. */
  chartData: ChartRow[];
  /** Pre-built `Map<timestamp, Record<topicId, value>>` pro O(1) tooltip lookup. */
  indexedByTimestamp: Map<number, Record<string, number | boolean | null>>;
  /** Min/max přes všechny topicy + bucket bounds. */
  minMax: { minV: number; maxV: number; minT: number; maxT: number };
  /** Topic metadata (decimals, unit, nickname, valueType, suspicious). */
  topics: Record<string, MQTTValue["topic"]>;
  suspicious: Record<string, boolean>;
  /** True dokud běží alespoň jeden fetch. */
  loading: boolean;
}

export const useGraph2Data = (
  topicIds: string[],
  bounds: Bounds,
  pixelWidth: number,
  enabled: boolean
): Graph2DataResult => {
  const { series, resolution, loading } = useChartData({
    topicIds,
    start: bounds.min,
    end: bounds.max,
    pixelWidth,
    enabled,
  });
  const { topics, suspicious } = useTopics(topicIds);

  // Pre-build indexed map — O(N) per data change, O(1) per tooltip lookup
  const indexedByTimestamp = useMemo(() => {
    const idx = new Map<number, Record<string, number | boolean | null>>();
    for (const [topicId, points] of series) {
      for (const p of points) {
        let row = idx.get(p.ts);
        if (!row) {
          row = {};
          idx.set(p.ts, row);
        }
        row[topicId] = p.v;
      }
    }
    return idx;
  }, [series]);

  // chartData = sorted rows pro Recharts
  const chartData = useMemo(() => {
    const sortedTs = [...indexedByTimestamp.keys()].sort((a, b) => a - b);
    return sortedTs.map((timestamp) => {
      const row: ChartRow = { timestamp };
      const indexed = indexedByTimestamp.get(timestamp);
      if (indexed) {
        for (const id of topicIds) {
          const v = indexed[id];
          if (typeof v === "number") row[id] = v;
          else if (typeof v === "boolean") row[id] = v ? 1 : 0;
        }
      }
      return row;
    });
  }, [indexedByTimestamp, topicIds]);

  // min/max přes reduce — žádný Math.min(...arr) spread overflow
  const minMax = useMemo(() => {
    let minV = Infinity;
    let maxV = -Infinity;
    let minT = Infinity;
    let maxT = -Infinity;
    for (const points of series.values()) {
      for (const p of points) {
        const numeric =
          typeof p.v === "number" ? p.v : typeof p.v === "boolean" ? (p.v ? 1 : 0) : null;
        if (numeric !== null) {
          if (numeric < minV) minV = numeric;
          if (numeric > maxV) maxV = numeric;
        }
        // POZN: bucket min/max pásmo (p.min/p.max) se NErenderuje (Area má fillOpacity={0}),
        // takže ho do Y-rozsahu nepočítáme — osa pak hugne skutečně kreslenou (avg/raw) čáru.
        if (p.ts < minT) minT = p.ts;
        if (p.ts > maxT) maxT = p.ts;
      }
    }
    if (minV === Infinity) {
      // No data — vrať placeholder, downstream useMemo (yDomain) testuje na Infinity
      return { minV: Number.MAX_SAFE_INTEGER, maxV: 0, minT: 0, maxT: 0 };
    }
    return { minV, maxV, minT, maxT };
  }, [series]);

  return { series, resolution, chartData, indexedByTimestamp, minMax, topics, suspicious, loading };
};
